import React, { Component } from 'react';
import { animate } from 'framer-motion';
import { Utils } from '../../services';

/**
 * GlowingEffect Component
 * 
 * @see https://ui.aceternity.com/components/glowing-effect
 */
class GlowingEffect extends Component {
    constructor(props) {
        super(props);
        this.containerRef = React.createRef();
        this.lastPosition = { x: 0, y: 0 };
        this.animationFrameRef = 0;
        this.currentAngle = 0;
    }

    componentDidMount() {
        if (this.props.disabled) return;
        
        this.handleScroll = () => this.handleMove();
        this.handlePointerMove = (e) => this.handleMove(e);
        
        window.addEventListener('scroll', this.handleScroll, { passive: true });
        document.body.addEventListener('pointermove', this.handlePointerMove, { passive: true });
    }

    componentWillUnmount() {
        if (this.animationFrameRef) {
            cancelAnimationFrame(this.animationFrameRef);
        }
        
        if (this.handleScroll) {
            window.removeEventListener('scroll', this.handleScroll);
        }
        
        if (this.handlePointerMove) {
            document.body.removeEventListener('pointermove', this.handlePointerMove);
        }
    }

    handleMove = (e) => {
        if (!this.containerRef.current) return;
        
        if (this.animationFrameRef) {
            cancelAnimationFrame(this.animationFrameRef);
        }
        
        this.animationFrameRef = requestAnimationFrame(() => {
            const element = this.containerRef.current;
            if (!element) return;
            
            const { left, top, width, height } = element.getBoundingClientRect();
            const mouseX = e?.x ?? this.lastPosition.x;
            const mouseY = e?.y ?? this.lastPosition.y;
            
            if (e) {
                this.lastPosition = { x: mouseX, y: mouseY };
            }
            
            const center = [left + width * 0.5, top + height * 0.5];
            const distanceFromCenter = Math.hypot(mouseX - center[0], mouseY - center[1]);
            const inactiveRadius = 0.5 * Math.min(width, height) * this.props.inactiveZone;
            
            if (distanceFromCenter < inactiveRadius) {
                element.style.setProperty('--active', '0');
                return;
            }
            
            const isActive =
                mouseX > left - this.props.proximity &&
                mouseX < left + width + this.props.proximity &&
                mouseY > top - this.props.proximity &&
                mouseY < top + height + this.props.proximity;
            
            element.style.setProperty('--active', isActive ? '1' : '0');
            
            if (!isActive) return;
            
            const currentAngle = parseFloat(element.style.getPropertyValue('--start')) || 0;
            let targetAngle =
                (180 * Math.atan2(mouseY - center[1], mouseX - center[0])) / Math.PI + 90;
            
            const angleDiff = ((targetAngle - currentAngle + 180) % 360) - 180;
            const newAngle = currentAngle + angleDiff;
            
            animate(currentAngle, newAngle, {
                duration: this.props.movementDuration,
                ease: [0.16, 1, 0.3, 1],
                onUpdate: (value) => {
                    element.style.setProperty('--start', String(value));
                },
            });
        });
    };

    cn(...args) {
        return Utils.classNames(...args);
    }

    render() {
        const {
            blur = 0,
            inactiveZone = 0.7,
            proximity = 0,
            spread = 20,
            variant = 'default',
            glow = false,
            className = '',
            movementDuration = 2,
            borderWidth = 1,
            disabled = true,
        } = this.props;

        const gradient =
            variant === 'white'
                ? `repeating-conic-gradient(
                    from 236.84deg at 50% 50%,
                    var(--black),
                    var(--black) calc(25% / var(--repeating-conic-gradient-times))
                )`
                : `radial-gradient(circle, #9ADBE8 10%, #9ADBE800 20%),
                radial-gradient(circle at 40% 40%, #004C97 5%, #004C9700 15%),
                radial-gradient(circle at 60% 60%, #8DE971 10%, #8DE97100 20%), 
                radial-gradient(circle at 40% 60%, #9ADBE8 10%, #9ADBE800 20%),
                repeating-conic-gradient(
                  from 236.84deg at 50% 50%,
                  #9ADBE8 0%,
                  #004C97 calc(25% / var(--repeating-conic-gradient-times)),
                  #8DE971 calc(50% / var(--repeating-conic-gradient-times)), 
                  #9ADBE8 calc(75% / var(--repeating-conic-gradient-times)),
                  #9ADBE8 calc(100% / var(--repeating-conic-gradient-times))
                )`;

        return (
            <>
                <div
                    className={this.cn(
                        'pointer-events-none absolute -inset-px hidden rounded-[inherit] border opacity-0 transition-opacity',
                        glow && 'opacity-100',
                        variant === 'white' && 'border-white',
                        disabled && '!block'
                    )}
                />
                <div
                    ref={this.containerRef}
                    style={{
                        '--blur': `${blur}px`,
                        '--spread': spread,
                        '--start': '0',
                        '--active': '0',
                        '--glowingeffect-border-width': `${borderWidth}px`,
                        '--repeating-conic-gradient-times': '3',
                        '--gradient': gradient,
                    }}
                    className={this.cn(
                        'pointer-events-none absolute inset-0 rounded-[inherit] opacity-100 transition-opacity',
                        glow && 'opacity-100',
                        blur > 0 && 'blur-[var(--blur)]',
                        className,
                        disabled && '!hidden'
                    )}
                >
                    <div
                        className={this.cn(
                            'glow',
                            'rounded-[inherit]',
                            'after:content-[""] after:rounded-[inherit] after:absolute after:inset-[calc(-1*var(--glowingeffect-border-width))]',
                            'after:[border:var(--glowingeffect-border-width)_solid_transparent]',
                            'after:[background:var(--gradient)] after:[background-attachment:fixed]',
                            'after:opacity-[var(--active)] after:transition-opacity after:duration-300',
                            'after:[mask-clip:padding-box,border-box]',
                            'after:[mask-composite:intersect]',
                            'after:[mask-image:linear-gradient(#0000,#0000),conic-gradient(from_calc((var(--start)-var(--spread))*1deg),#00000000_0deg,#fff,#00000000_calc(var(--spread)*2deg))]'
                        )}
                    />
                </div>
            </>
        );
    }
}

GlowingEffect.defaultProps = {
    blur: 0,
    inactiveZone: 0.7,
    proximity: 0,
    spread: 30,
    variant: 'default',
    glow: false,
    className: '',
    movementDuration: 2,
    borderWidth: 1,
    disabled: true,
};

export default GlowingEffect;

